import { useEffect, useState } from "preact/hooks"
import { parseISO, format } from "date-fns"
import { Trash2 } from "lucide-react"
import { api_fetch, api_fetch_throws } from "@/api"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { toast } from "@/components/ui/use-toast"
import { classes } from "@/utils"
import { SearchableSelect } from "@/components/searchable_select"
import { Input } from "@/components/ui/input"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"

export interface RuleMatchConditions {
  direct_customers: string[]
  indirect_customers: string[]
  direct_planned_customers: string[]
  indirect_planned_customers: string[]
  selling_units: string[]
  rate_dollar_from: number | null
  rate_dollar_to: number | null
  rate_pct_from: number | null
  rate_pct_to: number | null
  match_field: string | null
  starts_with: string | null
  contains: string | null
  ends_with: string | null
  string_rule_excluded: boolean
}

export interface RuleMapping {
  id: string
  category_id: string | null
  category_name: string | null
  category_description: string | null
  category_source: string | null
  category_invoice_regex: string | null
  category_remark_regex: string | null
  reason_code_id: string
  fund_name: string
  gl_account: string
  fund_category: string | null
  external_id: string | null
  enabled: boolean
  start_date: string | null
  end_date: string | null
  conditions: RuleMatchConditions
  updated_at: string
  created_at: string
  /** `user`, `accounting_wizard` (CodingWiz), an import label, or null for old rows. */
  actor: string | null
  actor_email: string | null
  history: RuleChange[]
  /** Lines auto-code wrote from this rule (recorded since 2026-09-28). */
  coded_count: number
  /** Deductions currently in the rule's category. */
  category_deduction_count: number
  example_invoice_numbers: string[]
}

export interface RuleChange {
  changed_at: string | null
  actor: string | null
  actor_email: string | null
  old_gl_account: string | null
  new_gl_account: string | null
}

interface CategoryOption {
  id: string
  name: string
  source: string
  description: string
}

export interface CategoryPattern {
  /** "invoice number" or "description" — which field the pattern is tested against. */
  field: string
  pattern: string
}

/**
 * The patterns that decide which deductions reach this mapping, in the order the matcher tries
 * them: `CategoryMap::lookup_by_fields` tests the invoice pattern first and only falls through to
 * the description one if that misses. A category with neither matches nothing on its own.
 */
export function categoryPatterns(
  rule: Pick<RuleMapping, "category_invoice_regex" | "category_remark_regex">,
): CategoryPattern[] {
  const patterns: CategoryPattern[] = []
  if (rule.category_invoice_regex) {
    patterns.push({ field: "invoice number", pattern: rule.category_invoice_regex })
  }
  if (rule.category_remark_regex) {
    patterns.push({ field: "description", pattern: rule.category_remark_regex })
  }
  return patterns
}

interface ReasonCodeOption {
  id: string
  account_number: string
  expense_account: string
}

export interface FundGroup {
  gl_account: string
  fund_name: string
  fund_category: string | null
  rules: RuleMapping[]
}

/** Group mappings into the Promomash "Fund" sections (one per GL account), sorted by fund name. */
export function groupMappingsByFund(mappings: RuleMapping[]): FundGroup[] {
  const groups = new Map<string, FundGroup>()
  for (const m of mappings) {
    const existing = groups.get(m.gl_account)
    if (existing) {
      existing.rules.push(m)
    } else {
      groups.set(m.gl_account, { gl_account: m.gl_account, fund_name: m.fund_name, fund_category: m.fund_category, rules: [m] })
    }
  }
  return [...groups.values()].sort((a, b) => a.fund_name.localeCompare(b.fund_name, undefined, { sensitivity: "base" }))
}

/** Join all customer lists into a single comma-separated display string. */
export function formatCustomers(c: RuleMatchConditions): string {
  return [...c.direct_customers, ...c.indirect_customers, ...c.direct_planned_customers, ...c.indirect_planned_customers].join(", ")
}

/** "$1.00–$2.00 · 5%–10%" style summary of the rate ranges, or "" when none set. */
export function formatRateRange(c: RuleMatchConditions): string {
  const parts: string[] = []
  if (c.rate_dollar_from != null || c.rate_dollar_to != null) {
    parts.push(`$${c.rate_dollar_from ?? ""}–$${c.rate_dollar_to ?? ""}`)
  }
  if (c.rate_pct_from != null || c.rate_pct_to != null) {
    parts.push(`${c.rate_pct_from ?? ""}%–${c.rate_pct_to ?? ""}%`)
  }
  return parts.join(" · ")
}

/** "Field starts/contains/ends …" summary of the string rule, or "" when none set. */
export function formatStringRule(c: RuleMatchConditions): string {
  const clauses: string[] = []
  if (c.starts_with) clauses.push(`starts "${c.starts_with}"`)
  if (c.contains) clauses.push(`contains "${c.contains}"`)
  if (c.ends_with) clauses.push(`ends "${c.ends_with}"`)
  if (clauses.length === 0) return ""
  const prefix = c.match_field ? `${c.match_field}: ` : ""
  const excluded = c.string_rule_excluded ? " (excluded)" : ""
  return `${prefix}${clauses.join(", ")}${excluded}`
}

/** Format a single ISO date for display, parsing with parseISO per project convention. */
function formatDate(iso: string | null): string {
  if (!iso) return ""
  return format(parseISO(iso), "MMM d, yyyy")
}

export function formatDateRange(start: string | null, end: string | null): string {
  if (!start && !end) return ""
  return `${formatDate(start) || "—"} → ${formatDate(end) || "—"}`
}

export function ReasonCodeMappingSection() {
  const [mappings, setMappings] = useState<RuleMapping[]>([])
  const [categories, setCategories] = useState<CategoryOption[]>([])
  const [reasonCodes, setReasonCodes] = useState<ReasonCodeOption[]>([])
  const [loading, setLoading] = useState(false)

  async function load() {
    setLoading(true)
    try {
      const [rules, cats, codes] = await Promise.all([
        api_fetch_throws<RuleMapping[]>("/accounting/rules", { use_cache: false }),
        api_fetch_throws<CategoryOption[]>("/categories", { use_cache: false }),
        api_fetch_throws<ReasonCodeOption[]>("/reason_codes?include_hidden=true", { use_cache: false }),
      ])
      setMappings(rules)
      setCategories(cats)
      setReasonCodes(codes)
    } finally {
      setLoading(false)
    }
  }

  useEffect(function init() {
    void load()
  }, [])

  function handleCreated(mapping: RuleMapping) {
    setMappings(prev => [...prev, mapping])
  }

  function handleUpdated(mapping: RuleMapping) {
    setMappings(prev => prev.map(m => (m.id === mapping.id ? mapping : m)))
  }

  function handleDeleted(id: string) {
    setMappings(prev => prev.filter(m => m.id !== id))
  }

  const groups = groupMappingsByFund(mappings)

  return (
    <div class="bg-white rounded-lg shadow-sm p-6 space-y-6">
      <div>
        <h2 class="text-lg font-semibold leading-7 text-gray-900">Reason code mapping</h2>
        <p class="text-sm text-gray-500">
          How deduction categories map to accounting reason codes (Funds / GL accounts). Upload a Promomash Funds export
          above to populate this, or add mappings manually.
        </p>
      </div>

      <ReasonCodeMappingForm categories={categories} reasonCodes={reasonCodes} onCreated={handleCreated} />

      {loading ? (
        <div class="text-sm text-gray-500">Loading…</div>
      ) : groups.length === 0 ? (
        <div class="text-sm text-gray-500">No mappings yet.</div>
      ) : (
        <div class="space-y-8">
          {groups.map(group => (
            <FundGroupTable key={group.gl_account} group={group} onUpdated={handleUpdated} onDeleted={handleDeleted} />
          ))}
        </div>
      )}
    </div>
  )
}

interface FundGroupTableProps {
  group: FundGroup
  onUpdated: (mapping: RuleMapping) => void
  onDeleted: (id: string) => void
}

function FundGroupTable({ group, onUpdated, onDeleted }: FundGroupTableProps) {
  return (
    <div>
      <div class="flex items-baseline gap-2 mb-2">
        <h3 class="text-base font-semibold text-gray-900">{group.fund_name}</h3>
        <span class="text-xs font-mono text-gray-500">{group.gl_account}</span>
        {group.fund_category ? (
          <span class="text-xs rounded-full bg-gray-100 px-2 py-0.5 text-gray-600">{group.fund_category}</span>
        ) : null}
      </div>
      <div class="overflow-x-auto border rounded-md">
        <table class="min-w-full divide-y divide-gray-200 text-sm">
          <thead class="bg-gray-50">
            <tr>
              <Th>Reason code (category)</Th>
              <Th>Matches</Th>
              <Th>Customers</Th>
              <Th>Selling units</Th>
              <Th>Dates</Th>
              <Th>Rate</Th>
              <Th>String rule</Th>
              <Th>Enabled</Th>
              <th class="px-3 py-2" />
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            {group.rules.map(rule => (
              <MappingRow key={rule.id} rule={rule} onUpdated={onUpdated} onDeleted={onDeleted} />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function Th({ children }: { children: preact.ComponentChildren }) {
  return <th class="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">{children}</th>
}

interface MappingRowProps {
  rule: RuleMapping
  onUpdated: (mapping: RuleMapping) => void
  onDeleted: (id: string) => void
}

function MappingRow({ rule, onUpdated, onDeleted }: MappingRowProps) {
  const [saving, setSaving] = useState(false)

  async function toggleEnabled(next: boolean) {
    setSaving(true)
    const res = await api_fetch<RuleMapping>(`/accounting/rules/${rule.id}`, { method: "PUT", body: { enabled: next } })
    if (res.ok) {
      onUpdated(res.value.data)
    } else {
      toast({ title: "Could not update", description: res.error.message, variant: "destructive" })
    }
    setSaving(false)
  }

  async function deleteRow() {
    const res = await api_fetch(`/accounting/rules/${rule.id}`, { method: "DELETE" })
    if (res.ok) {
      onDeleted(rule.id)
      toast({ title: "Deleted", description: "Mapping removed" })
    } else {
      toast({ title: "Failed to delete", description: res.error.message, variant: "destructive" })
    }
  }

  const customers = formatCustomers(rule.conditions)
  const selling_units = rule.conditions.selling_units.join(", ")
  const rate = formatRateRange(rule.conditions)
  const string_rule = formatStringRule(rule.conditions)
  const dates = formatDateRange(rule.start_date, rule.end_date)
  const patterns = categoryPatterns(rule)

  return (
    <tr class={rule.enabled ? "" : "opacity-50"}>
      <td class="px-3 py-2 align-top max-w-[18rem]">
        {rule.category_name ?? <span class="text-gray-400">{string_rule ? "Any category" : "—"}</span>}
        {rule.category_description ? <div class="text-xs text-gray-500 mt-0.5">{rule.category_description}</div> : null}
      </td>
      <td class="px-3 py-2 align-top max-w-[22rem]">
        {patterns.length === 0 ? (
          <span class="text-xs text-gray-400">Matches nothing on its own</span>
        ) : (
          <div class="space-y-0.5">
            {patterns.map(p => (
              <div key={p.field} class="text-xs text-gray-600">
                <span class="text-gray-400">{p.field}</span>{" "}
                <code class="font-mono text-gray-800 break-all">{p.pattern}</code>
              </div>
            ))}
          </div>
        )}
        {rule.category_source ? (
          <span class="inline-block mt-1 text-[11px] rounded-full bg-gray-100 px-1.5 py-0.5 text-gray-500">{rule.category_source}</span>
        ) : null}
      </td>
      <td class="px-3 py-2 align-top max-w-[16rem] truncate" title={customers}>{customers || <Dash />}</td>
      <td class="px-3 py-2 align-top max-w-[14rem] truncate" title={selling_units}>{selling_units || <Dash />}</td>
      <td class="px-3 py-2 align-top whitespace-nowrap">{dates || <Dash />}</td>
      <td class="px-3 py-2 align-top whitespace-nowrap">{rate || <Dash />}</td>
      <td class="px-3 py-2 align-top max-w-[16rem] truncate" title={string_rule}>{string_rule || <Dash />}</td>
      <td class="px-3 py-2 align-middle">
        <Checkbox
          checked={rule.enabled}
          disabled={saving}
          onCheckedChange={(v: boolean | "indeterminate") => {
            if (v === "indeterminate") return
            void toggleEnabled(v)
          }}
        />
      </td>
      <td class="px-3 py-2 text-right">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button class={classes("p-1.5 rounded-full transition-colors cursor-pointer hover:bg-red-50")} aria-label="Delete mapping">
              <Trash2 className="h-4 w-4 text-red-500 hover:text-red-600" />
            </button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete mapping?</AlertDialogTitle>
              <AlertDialogDescription>This removes the category → reason-code mapping. It cannot be undone.</AlertDialogDescription>
            </AlertDialogHeader>
            <div class="flex justify-end gap-2 mt-4">
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deleteRow as any}>Delete</AlertDialogAction>
            </div>
          </AlertDialogContent>
        </AlertDialog>
      </td>
    </tr>
  )
}

function Dash() {
  return <span class="text-gray-300">—</span>
}

interface ReasonCodeMappingFormProps {
  categories: CategoryOption[]
  reasonCodes: ReasonCodeOption[]
  onCreated: (mapping: RuleMapping) => void
}

function ReasonCodeMappingForm({ categories, reasonCodes, onCreated }: ReasonCodeMappingFormProps) {
  const [categoryId, setCategoryId] = useState("")
  const [reasonCodeId, setReasonCodeId] = useState("")
  const [match_kind, set_match_kind] = useState<DescriptionMatchKind>("starts_with")
  const [match_text, set_match_text] = useState("")
  const [saving, setSaving] = useState(false)

  const categoryOptions = [
    ...(match_text.trim() ? [{ value: ANY_CATEGORY, label: "Any category" }] : []),
    ...categories.map(c => ({ value: c.id, label: c.name })),
  ]
  const reasonCodeOptions = reasonCodes.map(r => ({ value: r.id, label: `${r.account_number} — ${r.expense_account}` }))

  async function handleSubmit(e: Event) {
    e.preventDefault()
    const any_category = categoryId === ANY_CATEGORY || (!categoryId && Boolean(match_text.trim()))
    if ((!categoryId && !any_category) || !reasonCodeId) {
      toast({ title: "Missing fields", description: "Pick a reason code, and a category or a description match", variant: "destructive" })
      return
    }
    setSaving(true)
    const res = await api_fetch<RuleMapping>("/accounting/rules", {
      method: "POST",
      body: {
        category_id: any_category ? null : categoryId,
        reason_code_id: reasonCodeId,
        conditions: description_conditions(match_kind, match_text),
      },
    })
    if (res.ok) {
      onCreated(res.value.data)
      toast({ title: "Created", description: "Mapping added" })
      setCategoryId("")
      setReasonCodeId("")
      set_match_text("")
    } else {
      toast({ title: "Failed to create", description: res.error.message, variant: "destructive" })
    }
    setSaving(false)
  }

  return (
    <form onSubmit={handleSubmit as any} class="grid grid-cols-1 md:grid-cols-7 gap-4 items-end border-b pb-6">
      <div class="md:col-span-3">
        <Label>Deduction category</Label>
        <SearchableSelect
          options={categoryOptions}
          value={categoryId}
          onValueChange={setCategoryId}
          placeholder="Select category…"
          searchPlaceholder="Search categories…"
        />
      </div>
      <div class="md:col-span-3">
        <Label>Reason code (Fund / GL)</Label>
        <SearchableSelect
          options={reasonCodeOptions}
          value={reasonCodeId}
          onValueChange={setReasonCodeId}
          placeholder="Select reason code…"
          searchPlaceholder="Search reason codes…"
        />
      </div>
      <div class="md:col-span-1">
        <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Add"}</Button>
      </div>
      <div class="md:col-span-6 flex flex-wrap items-end gap-2">
        <div>
          <Label>Only when the description</Label>
          <Select value={match_kind} onValueChange={(v: string) => set_match_kind(v as DescriptionMatchKind)}>
            <SelectTrigger class="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="starts_with">starts with</SelectItem>
              <SelectItem value="contains">contains</SelectItem>
              <SelectItem value="ends_with">ends with</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Input
          class="w-64"
          value={match_text}
          placeholder={`e.g. "110 " or a contract number`}
          onInput={(e: Event) => set_match_text((e.target as HTMLInputElement).value)}
        />
        <p class="text-xs text-gray-500 basis-full">
          Optional. A description rule wins over the category's other mappings for the deductions it matches — key it on the
          retailer's own reason code. With "Any category" it applies whatever the deduction is categorized as.
        </p>
      </div>
    </form>
  )
}

const ANY_CATEGORY = "__any__"

type DescriptionMatchKind = "starts_with" | "contains" | "ends_with"

/** The conditions a new mapping is created with: a description string rule, or none when blank. */
export function description_conditions(kind: DescriptionMatchKind, text: string): RuleMatchConditions | null {
  const value = text.trim() ? text : ""
  if (!value) return null
  return {
    direct_customers: [],
    indirect_customers: [],
    direct_planned_customers: [],
    indirect_planned_customers: [],
    selling_units: [],
    rate_dollar_from: null,
    rate_dollar_to: null,
    rate_pct_from: null,
    rate_pct_to: null,
    match_field: "Description",
    starts_with: kind === "starts_with" ? value : null,
    contains: kind === "contains" ? value : null,
    ends_with: kind === "ends_with" ? value : null,
    string_rule_excluded: false,
  }
}

// Inline tests
// @vitest-environment jsdom
if (import.meta.vitest) {
  const { it, expect } = import.meta.vitest

  function emptyConditions(): RuleMatchConditions {
    return {
      direct_customers: [],
      indirect_customers: [],
      direct_planned_customers: [],
      indirect_planned_customers: [],
      selling_units: [],
      rate_dollar_from: null,
      rate_dollar_to: null,
      rate_pct_from: null,
      rate_pct_to: null,
      match_field: null,
      starts_with: null,
      contains: null,
      ends_with: null,
      string_rule_excluded: false,
    }
  }

  function mapping(partial: Partial<RuleMapping>): RuleMapping {
    return {
      id: "1",
      category_id: "c",
      category_name: "Scan",
      category_description: "Scan promo",
      category_source: "kehe",
      category_invoice_regex: null,
      category_remark_regex: null,
      reason_code_id: "r",
      fund_name: "COS MCB: Snacks",
      gl_account: "02-4606-01",
      fund_category: "Snacks",
      external_id: null,
      enabled: true,
      start_date: null,
      end_date: null,
      conditions: emptyConditions(),
      updated_at: "2026-01-01T00:00:00Z",
      created_at: "2026-01-01T00:00:00Z",
      actor: null,
      actor_email: null,
      history: [],
      coded_count: 0,
      category_deduction_count: 0,
      example_invoice_numbers: [],
      ...partial,
    }
  }

  it("categoryPatterns lists the invoice pattern before the description one", () => {
    // `lookup_by_fields` tries the invoice regex first for a source like C&S, so the order shown
    // has to be the order the matcher actually applies.
    const patterns = categoryPatterns({ category_invoice_regex: "DC\\d+", category_remark_regex: "RECLAMATION.*" })
    expect(patterns).toEqual([
      { field: "invoice number", pattern: "DC\\d+" },
      { field: "description", pattern: "RECLAMATION.*" },
    ])
  })

  it("categoryPatterns omits a pattern the category does not have", () => {
    expect(categoryPatterns({ category_invoice_regex: null, category_remark_regex: "7350 - FLYER.*" })).toEqual([
      { field: "description", pattern: "7350 - FLYER.*" },
    ])
    expect(categoryPatterns({ category_invoice_regex: "CLA\\d+", category_remark_regex: null })).toEqual([
      { field: "invoice number", pattern: "CLA\\d+" },
    ])
  })

  it("categoryPatterns returns nothing for a category with no pattern at all", () => {
    // Rendered as "Matches nothing on its own" -- such a category only ever arrives by hand.
    expect(categoryPatterns({ category_invoice_regex: null, category_remark_regex: null })).toEqual([])
  })

  it("groupMappingsByFund groups by gl_account and sorts by fund name", () => {
    const groups = groupMappingsByFund([
      mapping({ id: "1", gl_account: "02-4606-01", fund_name: "COS MCB: Snacks" }),
      mapping({ id: "2", gl_account: "02-4600-01", fund_name: "COS Ads: Snacks" }),
      mapping({ id: "3", gl_account: "02-4606-01", fund_name: "COS MCB: Snacks", category_name: "Coupons" }),
    ])
    expect(groups.map(g => g.gl_account)).toEqual(["02-4600-01", "02-4606-01"])
    expect(groups[1].rules.map(r => r.id)).toEqual(["1", "3"])
  })

  it("formatCustomers joins all customer lists", () => {
    const c = { ...emptyConditions(), direct_customers: ["Wakefern", "H-E-B"], indirect_customers: ["UNFI"] }
    expect(formatCustomers(c)).toBe("Wakefern, H-E-B, UNFI")
  })

  it("formatRateRange formats dollar and percent ranges", () => {
    const c = { ...emptyConditions(), rate_dollar_from: 1, rate_dollar_to: 2, rate_pct_from: 5, rate_pct_to: 10 }
    expect(formatRateRange(c)).toBe("$1–$2 · 5%–10%")
    expect(formatRateRange(emptyConditions())).toBe("")
  })

  it("description_conditions builds one description clause, or none when blank", () => {
    expect(description_conditions("starts_with", "   ")).toBeNull()
    const c = description_conditions("starts_with", "110 ")
    expect(c?.starts_with).toBe("110 ")
    expect(c?.contains).toBeNull()
    expect(formatStringRule(c!)).toBe('Description: starts "110 "')
  })

  it("formatStringRule summarizes field clauses", () => {
    const c = { ...emptyConditions(), match_field: "Description", contains: "PROMO", string_rule_excluded: true }
    expect(formatStringRule(c)).toBe('Description: contains "PROMO" (excluded)')
    expect(formatStringRule(emptyConditions())).toBe("")
  })

  it("formatDateRange renders both ends", () => {
    expect(formatDateRange(null, null)).toBe("")
    expect(formatDateRange("2025-06-20T12:00:00Z", null)).toBe("Jun 20, 2025 → —")
  })

}
